/**
 * useCurriculumStandards — 성취기준 catalog와 과목 데이터를 읽는다.
 *
 * - `enabled`가 false면 아무것도 요청하지 않고 상태를 비운다(모달이 닫힌 동안).
 * - catalog 에러와 과목 에러를 따로 들고, `error`는 둘 중 먼저 있는 것을 돌려준다.
 *   과목을 바꾸면 과목 에러만 지워져 "과목 없음" 안내가 다른 과목까지 따라가지 않는다.
 * - `retry()`는 catalog·과목을 모두 다시 요청한다.
 * - 응답이 늦게 도착한 요청은 cancelled 플래그로 무시한다(과목을 빠르게 바꿀 때 stale 응답 방지).
 */

import { useCallback, useEffect, useState } from 'react';
import { fetchCurriculumCatalog, fetchSubjectStandards } from '../api/curriculumApi';

const useCurriculumStandards = ({
  curriculum = '2022',
  enabled = true,
  includeCommentary = false,
  subjectId = null,
} = {}) => {
  const [catalog, setCatalog] = useState(null);
  const [catalogError, setCatalogError] = useState(null);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [subject, setSubject] = useState(null);
  const [subjectError, setSubjectError] = useState(null);
  const [subjectLoading, setSubjectLoading] = useState(false);
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    if (!enabled) {
      setCatalog(null);
      setCatalogError(null);
      setCatalogLoading(false);
      return undefined;
    }
    let cancelled = false;
    setCatalog(null);
    setCatalogError(null);
    setCatalogLoading(true);
    (async () => {
      const result = await fetchCurriculumCatalog({ curriculum });
      if (cancelled) return;
      if (result.ok) {
        setCatalog(result.data);
        setCatalogError(null);
      } else {
        setCatalogError(result.error);
      }
      setCatalogLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [curriculum, enabled, reloadToken]);

  useEffect(() => {
    if (!enabled || !subjectId) {
      setSubject(null);
      setSubjectError(null);
      setSubjectLoading(false);
      return undefined;
    }
    let cancelled = false;
    setSubject(null);
    setSubjectError(null);
    setSubjectLoading(true);
    (async () => {
      const result = await fetchSubjectStandards(subjectId, { curriculum, includeCommentary });
      if (cancelled) return;
      if (result.ok) {
        setSubject(result.data);
      } else {
        setSubjectError(result.error);
      }
      setSubjectLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [curriculum, enabled, includeCommentary, reloadToken, subjectId]);

  const retry = useCallback(() => {
    setReloadToken((token) => token + 1);
  }, []);

  return {
    catalog,
    catalogError,
    error: catalogError || subjectError,
    loading: Boolean(enabled && (catalogLoading || subjectLoading)),
    retry,
    subject,
    subjectError,
  };
};

export default useCurriculumStandards;
